const normalizeValue = (value: string | undefined) => value?.trim().toLowerCase() ?? '';

export type LlmProviderKey = 'openai' | 'groq' | 'google' | 'xai' | 'moonshotai' | 'deepseek';

interface LlmModelDefinition {
  key: string;
  label: string;
  description?: string;
}

interface LlmModelPricing {
  input: string;
  cachedInput?: string;
  output: string;
}

const LLM_MODEL_PRICING: Record<string, LlmModelPricing> = {
  // OpenAI
  'chat-latest': { input: '5.000', cachedInput: '0.500', output: '30.000' },
  'gpt-4.1': { input: '2.000', cachedInput: '0.500', output: '8.000' },
  'gpt-4.1-mini': { input: '0.400', cachedInput: '0.100', output: '1.600' },
  'gpt-4.1-nano': { input: '0.100', cachedInput: '0.030', output: '0.400' },
  'gpt-4o': { input: '2.500', cachedInput: '1.250', output: '10.000' },
  'gpt-4o-mini': { input: '0.150', cachedInput: '0.075', output: '0.600' },
  'gpt-5': { input: '1.250', cachedInput: '0.125', output: '10.000' },
  'gpt-5-mini': { input: '0.250', cachedInput: '0.025', output: '2.000' },
  'gpt-5-nano': { input: '0.050', cachedInput: '0.005', output: '0.400' },
  'gpt-5.1': { input: '1.250', cachedInput: '0.125', output: '10.000' },
  'gpt-5.1-chat-latest': { input: '1.250', cachedInput: '0.125', output: '10.000' },
  'gpt-5.2': { input: '1.750', cachedInput: '0.175', output: '14.000' },
  'gpt-5.2-chat-latest': { input: '1.750', cachedInput: '0.175', output: '14.000' },
  'gpt-5.3-chat-latest': { input: '1.750', cachedInput: '0.175', output: '14.000' },
  'gpt-5.4': { input: '5.000', cachedInput: '0.500', output: '22.500' },
  'gpt-5.4-mini': { input: '0.750', cachedInput: '0.075', output: '4.500' },
  'gpt-5.4-nano': { input: '0.200', cachedInput: '0.020', output: '1.250' },
  'gpt-5.5': { input: '10.000', cachedInput: '1.000', output: '45.000' },
  'gpt-5.6-sol': { input: '4.000', cachedInput: '0.400', output: '20.000' },
  'gpt-5.6-terra': { input: '2.000', cachedInput: '0.200', output: '12.000' },
  'gpt-5.6-luna': { input: '0.200', cachedInput: '0.020', output: '1.200' },
  'gpt-6-astra': { input: '10.000', cachedInput: '1.000', output: '50.000' },
  'gpt-6.1-sol': { input: '2.000', cachedInput: '0.100', output: '10.000' },
  'gpt-6-sol': { input: '2.000', cachedInput: '0.200', output: '10.000' },
  'gpt-6-luna': { input: '0.100', cachedInput: '0.010', output: '0.500' },
  'gpt-oss-120b': { input: '0.100', output: '0.500' },
  // Google
  'gemini-2.5-flash': { input: '0.300', cachedInput: '0.030', output: '2.500' },
  'gemini-2.5-flash-lite': { input: '0.100', cachedInput: '0.010', output: '0.400' },
  'gemini-2.5-pro': { input: '2.500', cachedInput: '0.250', output: '15.000' },
  'gemini-3-flash-preview': { input: '0.500', cachedInput: '0.050', output: '3.000' },
  'gemini-3.1-flash-lite': { input: '0.250', cachedInput: '0.025', output: '1.500' },
  'gemini-3.1-pro-preview': { input: '4.000', cachedInput: '0.400', output: '18.000' },
  'gemini-3.5-flash': { input: '1.500', cachedInput: '0.150', output: '9.000' },
  'gemini-3.5-flash-lite': { input: '0.300', cachedInput: '0.030', output: '2.500' },
  'gemini-3.6-flash': { input: '1.500', cachedInput: '0.150', output: '7.500' },
  'gemini-3.7-flash': { input: '0.750', cachedInput: '0.075', output: '3.750' },
  'gemini-3.8-flash': { input: '0.750', cachedInput: '0.075', output: '3.750' },
  'gemini-4-argon': { input: '2.000', cachedInput: '0.100', output: '10.000' },
  'gemma-4-31b-it': { input: '0.400', cachedInput: '0.200', output: '1.200' },
  // xAI
  'grok-4-1-fast-non-reasoning': { input: '0.200', output: '0.500' },
  'grok-4-1-fast-reasoning': { input: '0.200', output: '0.500' },
  'grok-4.20-0309-non-reasoning': { input: '2.000', output: '6.000' },
  'grok-4.20-0309-reasoning': { input: '2.000', output: '6.000' },
  'grok-4.20-multi-agent-0309': { input: '2.000', output: '6.000' },
  'grok-4.3': { input: '1.250', cachedInput: '0.200', output: '2.500' },
  'grok-4.5': { input: '2.000', cachedInput: '0.500', output: '6.000' },
  'grok-4.6': { input: '2.000', cachedInput: '0.500', output: '6.000' },
  'grok-4.7': { input: '2.000', cachedInput: '0.500', output: '6.000' },
  // MoonshotAI
  'kimi-k2.5': { input: '0.600', cachedInput: '0.120', output: '3.000' },
  'kimi-k2.6': { input: '0.950', output: '4.000' },
  // DeepSeek
  'deepseek-v4-pro': { input: '1.740', cachedInput: '0.150', output: '3.480' },
  'deepseek-v4.1-flash': { input: '0.300', cachedInput: '0.030', output: '1.200' },
  // Groq
  'openai/gpt-oss-120b': { input: '0.150', cachedInput: '0.075', output: '0.600' },
};

const formatLlmPricing = (p: LlmModelPricing): string => {
  const parts = [`$${p.input} in`];
  if (p.cachedInput) parts.push(`$${p.cachedInput} cached`);
  parts.push(`$${p.output} out`);
  return `${parts.join(' • ')} /M tokens`;
};

const withPricing = (models: Omit<LlmModelDefinition, 'description'>[]): LlmModelDefinition[] =>
  models.map((m) => {
    const pricing = LLM_MODEL_PRICING[m.key];
    return pricing ? { ...m, description: formatLlmPricing(pricing) } : m;
  });

const LLM_MODELS_BY_PROVIDER: Record<LlmProviderKey, LlmModelDefinition[]> = {
  openai: withPricing([
    { key: 'gpt-4.1', label: 'GPT-4.1' },
    { key: 'gpt-4.1-mini', label: 'GPT-4.1 mini' },
    { key: 'gpt-4.1-nano', label: 'GPT-4.1 nano' },
    { key: 'gpt-4o', label: 'GPT-4o' },
    { key: 'gpt-4o-mini', label: 'GPT-4o mini' },
    { key: 'gpt-5', label: 'GPT-5' },
    { key: 'gpt-5-mini', label: 'GPT-5 mini' },
    { key: 'gpt-5-nano', label: 'GPT-5 nano' },
    { key: 'gpt-5.1', label: 'GPT-5.1' },
    { key: 'gpt-5.1-chat-latest', label: 'GPT-5.1 Chat' },
    { key: 'gpt-5.2', label: 'GPT-5.2' },
    { key: 'gpt-5.2-chat-latest', label: 'GPT-5.2 Chat' },
    { key: 'gpt-5.3-chat-latest', label: 'GPT-5.3 Chat' },
    { key: 'gpt-5.4', label: 'GPT-5.4' },
    { key: 'gpt-5.4-mini', label: 'GPT-5.4 mini' },
    { key: 'gpt-5.4-nano', label: 'GPT-5.4 nano' },
    { key: 'gpt-5.5', label: 'GPT-5.5' },
    { key: 'gpt-5.6-sol', label: 'GPT-5.6 Sol' },
    { key: 'gpt-5.6-terra', label: 'GPT-5.6 Terra' },
    { key: 'gpt-5.6-luna', label: 'GPT-5.6 Luna' },
    { key: 'gpt-6-astra', label: 'GPT-6 Astra' },
    { key: 'gpt-6.1-sol', label: 'GPT-6.1 Sol' },
    { key: 'gpt-6-sol', label: 'GPT-6 Sol' },
    { key: 'gpt-6-luna', label: 'GPT-6 Luna' },
    { key: 'chat-latest', label: 'ChatGPT Latest' },
    { key: 'gpt-oss-120b', label: 'GPT OSS 120B' },
  ]),
  google: withPricing([
    { key: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
    { key: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash-Lite' },
    { key: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
    { key: 'gemini-3-flash-preview', label: 'Gemini 3 Flash' },
    { key: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash Lite' },
    { key: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash' },
    { key: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash Lite' },
    { key: 'gemini-3.6-flash', label: 'Gemini 3.6 Flash' },
    { key: 'gemini-3.7-flash', label: 'Gemini 3.7 Flash' },
    { key: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash' },
    { key: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro' },
    { key: 'gemini-4-argon', label: 'Gemini 4 Argon' },
    { key: 'gemma-4-31b-it', label: 'Gemma 4 31B IT' },
  ]),
  xai: withPricing([
    { key: 'grok-4-1-fast-non-reasoning', label: 'Grok 4.1 Fast' },
    { key: 'grok-4-1-fast-reasoning', label: 'Grok 4.1 Fast Reasoning' },
    { key: 'grok-4.20-0309-non-reasoning', label: 'Grok 4.20' },
    { key: 'grok-4.20-0309-reasoning', label: 'Grok 4.20 Reasoning' },
    { key: 'grok-4.20-multi-agent-0309', label: 'Grok 4.20 Multi-Agent' },
    { key: 'grok-4.3', label: 'Grok 4.3' },
    { key: 'grok-4.5', label: 'Grok 4.5' },
    { key: 'grok-4.6', label: 'Grok 4.6' },
    { key: 'grok-4.7', label: 'Grok 4.7' },
  ]),
  moonshotai: withPricing([
    { key: 'kimi-k2.5', label: 'Kimi K2.5' },
    { key: 'kimi-k2.6', label: 'Kimi K2.6' },
  ]),
  deepseek: withPricing([
    { key: 'deepseek-v4-pro', label: 'DeepSeek V4 Pro' },
    { key: 'deepseek-v4.1-flash', label: 'DeepSeek V4.1 Flash' },
  ]),
  groq: withPricing([
    { key: 'llama-3.3-70b-versatile', label: 'Llama 3.3 70B Versatile' },
    { key: 'llama-3.1-8b-instant', label: 'Llama 3.1 8B Instant' },
    { key: 'openai/gpt-oss-20b', label: 'GPT OSS 20B' },
    { key: 'openai/gpt-oss-120b', label: 'GPT OSS 120B' },
    { key: 'openai/gpt-oss-safeguard-20b', label: 'GPT OSS Safeguard 20B' },
    { key: 'groq/compound', label: 'Groq Compound' },
    { key: 'groq/compound-mini', label: 'Groq Compound Mini' },
    { key: 'qwen/qwen3-32b', label: 'Qwen3 32B' },
  ]),
};

const LLM_PROVIDER_KEYS = new Set<string>(Object.keys(LLM_MODELS_BY_PROVIDER));

const isLlmProviderKey = (provider: string | undefined): provider is LlmProviderKey =>
  provider !== undefined && LLM_PROVIDER_KEYS.has(provider);

export const getLlmModelDefinitionsForProvider = (provider: string | undefined): LlmModelDefinition[] => {
  if (!isLlmProviderKey(provider)) return [];
  return LLM_MODELS_BY_PROVIDER[provider];
};

export const getLlmModelOptionsForProvider = (provider: string | undefined) =>
  getLlmModelDefinitionsForProvider(provider).map(({ key, label, description }) => ({
    key,
    label,
    description,
  }));

export const isLlmModelValidForProvider = (model: string | undefined, provider: string | undefined) => {
  const normalizedModel = normalizeValue(model);
  if (!normalizedModel || !isLlmProviderKey(provider)) return false;

  return getLlmModelDefinitionsForProvider(provider).some(
    (entry) => normalizeValue(entry.key) === normalizedModel,
  );
};

const REASONING_MODELS = new Set([
  'gpt-5',
  'gpt-5-mini',
  'gpt-5-nano',
  'gpt-5.1',
  'gpt-5.1-chat-latest',
  'gpt-5.2',
  'gpt-5.2-chat-latest',
  'gpt-5.3-chat-latest',
  'gpt-5.4',
  'gpt-5.4-mini',
  'gpt-5.4-nano',
  'gpt-5.5',
  'gpt-5.6-sol',
  'gpt-5.6-terra',
  'gpt-5.6-luna',
  'gpt-6-astra',
  'gpt-6.1-sol',
  'gpt-6-sol',
  'gpt-6-luna',
  'grok-4-1-fast-reasoning',
  'grok-4.20-0309-reasoning',
  'grok-4.20-multi-agent-0309',
  'gpt-oss-120b',
]);

export const supportsReasoningEffort = (model: string | undefined) => {
  const normalizedModel = normalizeValue(model);

  if (!normalizedModel) return false;
  return REASONING_MODELS.has(normalizedModel);
};

export const supportsTemperature = (model: string | undefined) => !supportsReasoningEffort(model);
